import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable } from '@/components/common/DataTable';
import { carrierAPI, contractTypeAPI, levelAPI } from '@/lib/api';
import { isKRRole, PRODUCT_LINES } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Settings,
  Plus,
  Building2,
  FileText,
  Layers,
  Loader2,
  Edit,
  Trash2,
  Save,
  X
} from 'lucide-react';

export default function CatalogPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [carriers, setCarriers] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [levels, setLevels] = useState([]);

  // Search & filter state for Carriers tab
  const [carrierSearch, setCarrierSearch] = useState('');
  const [carrierFilters, setCarrierFilters] = useState({});

  // Search & filter state for Contract Types tab
  const [ctSearch, setCtSearch] = useState('');
  const [ctFilters, setCtFilters] = useState({});

  // Dialog states
  const [showCarrierDialog, setShowCarrierDialog] = useState(false);
  const [showContractTypeDialog, setShowContractTypeDialog] = useState(false);

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', item: null });

  // Form states
  const [carrierForm, setCarrierForm] = useState({ carrier_name: '', notes: '', active: true });
  const [contractTypeForm, setContractTypeForm] = useState({ carrier_id: '', contract_type_name: '', description: '', active: true, product_line: 'ACA' });

  const [editingItem, setEditingItem] = useState(null);

  // Level Manager State - table-based editing
  const [levelEdits, setLevelEdits] = useState([]); // Current edits
  const [levelsToDelete, setLevelsToDelete] = useState([]); // IDs to delete
  const [levelManagerCarrier, setLevelManagerCarrier] = useState(''); // Filter by carrier
  const [hasLevelChanges, setHasLevelChanges] = useState(false);

  const isKR = isKRRole(user);

  useEffect(() => {
    loadAllData();
  }, []);

  // Initialize level edits when levels or carrier filter changes
  useEffect(() => {
    const filtered = levelManagerCarrier 
      ? levels.filter(l => l.carrier_id === levelManagerCarrier)
      : levels;
    setLevelEdits(filtered.map(l => ({ ...l, _isNew: false, _modified: false })));
    setLevelsToDelete([]);
    setHasLevelChanges(false);
  }, [levels, levelManagerCarrier]);

  const loadAllData = async () => {
    try {
      const [carriersRes, typesRes, levelsRes] = await Promise.all([
        carrierAPI.list(),
        contractTypeAPI.list(),
        levelAPI.list()
      ]);
      setCarriers(carriersRes.data);
      setContractTypes(typesRes.data);
      setLevels(levelsRes.data);
    } catch (error) {
      toast.error('Failed to load catalog data');
    } finally {
      setLoading(false);
    }
  };

  // Carrier handlers
  const handleCarrierSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await carrierAPI.update(editingItem.carrier_id, carrierForm);
        toast.success('Carrier updated');
      } else {
        await carrierAPI.create(carrierForm);
        toast.success('Carrier created');
      }
      setShowCarrierDialog(false);
      setEditingItem(null);
      setCarrierForm({ carrier_name: '', notes: '', active: true });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteCarrier = async () => {
    try {
      await carrierAPI.delete(deleteDialog.item.carrier_id);
      toast.success('Carrier deleted');
      setDeleteDialog({ open: false, type: '', item: null });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete carrier');
    }
  };

  // Contract Type handlers
  const handleContractTypeSubmit = async (e) => {
    e.preventDefault();
    try {
      // Phase 27.12 — Contract Type name === Product Line by design
      // (one type per carrier + type IS the product line). We derive the
      // name so the operator never has to type a redundant free-text label.
      const productLine = contractTypeForm.product_line || 'ACA';
      const payload = {
        carrier_id: contractTypeForm.carrier_id,
        contract_type_name: productLine,
        default_requirements: contractTypeForm.description,
        active: contractTypeForm.active,
        product_line: productLine,
      };
      if (editingItem) {
        await contractTypeAPI.update(editingItem.contract_type_id, payload);
        toast.success('Contract type updated');
      } else {
        await contractTypeAPI.create(payload);
        toast.success('Contract type created');
      }
      setShowContractTypeDialog(false);
      setEditingItem(null);
      setContractTypeForm({ carrier_id: '', contract_type_name: '', description: '', active: true, product_line: 'ACA' });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDeleteContractType = async () => {
    try {
      await contractTypeAPI.delete(deleteDialog.item.contract_type_id);
      toast.success('Contract type deleted');
      setDeleteDialog({ open: false, type: '', item: null });
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contract type');
    }
  };

  // Level Manager handlers
  const addLevelRow = () => {
    if (!levelManagerCarrier) {
      toast.error('Please select a carrier first');
      return;
    }
    const newRow = {
      level_id: null,
      carrier_id: levelManagerCarrier,
      level_name: '',
      level_code: '',
      level_rank: 0,
      active: true,
      _isNew: true,
      _modified: true
    };
    setLevelEdits(prev => [...prev, newRow]);
    setHasLevelChanges(true);
  };

  const updateLevelRow = (index, field, value) => {
    setLevelEdits(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value, _modified: true };
      return updated;
    });
    setHasLevelChanges(true);
  };

  const markLevelForDelete = (index) => {
    const level = levelEdits[index];
    if (level.level_id) {
      setLevelsToDelete(prev => [...prev, level.level_id]);
    }
    setLevelEdits(prev => prev.filter((_, i) => i !== index));
    setHasLevelChanges(true);
  };

  const handleBulkSaveLevels = async () => {
    // Validate
    for (const level of levelEdits) {
      if (!level.level_name.trim()) {
        toast.error('All levels must have a name');
        return;
      }
    }

    try {
      const levelsToSave = levelEdits.filter(l => l._modified).map(l => ({
        level_id: l.level_id,
        carrier_id: l.carrier_id,
        level_name: l.level_name,
        level_code: l.level_code,
        level_rank: parseInt(l.level_rank) || 0,
        active: l.active
      }));

      const response = await levelAPI.bulkSave(levelsToSave, levelsToDelete);
      toast.success(response.data.message);
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save levels');
    }
  };

  const cancelLevelChanges = () => {
    const filtered = levelManagerCarrier 
      ? levels.filter(l => l.carrier_id === levelManagerCarrier)
      : levels;
    setLevelEdits(filtered.map(l => ({ ...l, _isNew: false, _modified: false })));
    setLevelsToDelete([]);
    setHasLevelChanges(false);
  };

  // Filter defs for Carriers tab
  const carrierFilterDefs = useMemo(() => [
    { key: 'active', label: 'Status', options: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ]}
  ], []);

  const carrierFilterFn = useCallback((row, fv) => {
    if (fv.active && fv.active !== 'all') {
      const isActive = fv.active === 'true';
      if (row.active !== isActive) return false;
    }
    return true;
  }, []);

  // Filter defs for Contract Types tab
  const ctFilterDefs = useMemo(() => {
    const carrierOpts = carriers.filter(c => c.active).sort((a, b) => a.carrier_name.localeCompare(b.carrier_name))
      .map(c => ({ value: c.carrier_id, label: c.carrier_name }));
    return [
      { key: 'carrier_id', label: 'Carrier', options: carrierOpts },
      { key: 'active', label: 'Status', options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ]}
    ];
  }, [carriers]);

  const ctFilterFn = useCallback((row, fv) => {
    if (fv.carrier_id && fv.carrier_id !== 'all' && row.carrier_id !== fv.carrier_id) return false;
    if (fv.active && fv.active !== 'all') {
      const isActive = fv.active === 'true';
      if (row.active !== isActive) return false;
    }
    return true;
  }, []);

  const getCarrierName = (carrierId) => {
    const carrier = carriers.find(c => c.carrier_id === carrierId);
    return carrier?.carrier_name || '-';
  };

  const confirmDelete = () => {
    switch (deleteDialog.type) {
      case 'carrier':
        handleDeleteCarrier();
        break;
      case 'contractType':
        handleDeleteContractType();
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="catalog-page">
      <PageHeader 
        title="Catalog Manager"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Catalog', href: '/catalog' }
        ]}
      />
      
      <div className="page-content">
        <Card>
          <Tabs defaultValue="carriers">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <Settings size={20} className="text-slate-400" />
                  Configuration Catalog
                </CardTitle>
                <TabsList>
                  <TabsTrigger value="carriers" data-testid="tab-carriers">
                    <Building2 size={14} className="mr-1" />
                    Carriers
                  </TabsTrigger>
                  <TabsTrigger value="types" data-testid="tab-types">
                    <FileText size={14} className="mr-1" />
                    Contract Types
                  </TabsTrigger>
                  <TabsTrigger value="levels" data-testid="tab-levels">
                    <Layers size={14} className="mr-1" />
                    Carrier Levels
                  </TabsTrigger>
                </TabsList>
              </div>
            </CardHeader>
            <CardContent>
              {/* Carriers Tab */}
              <TabsContent value="carriers" className="mt-0">
                <div className="flex justify-end mb-4">
                  <Button onClick={() => {
                    setEditingItem(null);
                    setCarrierForm({ carrier_name: '', notes: '', active: true });
                    setShowCarrierDialog(true);
                  }} data-testid="add-carrier-btn">
                    <Plus size={16} className="mr-2" />
                    Add Carrier
                  </Button>
                </div>
                <DataTable
                  columns={[
                    { header: 'Carrier Name', accessor: 'carrier_name', sortable: true },
                    { header: 'Status', accessor: 'active', sortable: true, cell: (row) => <StatusBadge status={row.active ? 'active' : 'inactive'} /> },
                    { header: 'Notes', accessor: 'notes' },
                    { header: 'Actions', cell: (row) => (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingItem(row);
                          setCarrierForm({ carrier_name: row.carrier_name, notes: row.notes || '', active: row.active });
                          setShowCarrierDialog(true);
                        }} data-testid={`edit-carrier-${row.carrier_id}`}>
                          <Edit size={14} />
                        </Button>
                        {isKR && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => {
                            setDeleteDialog({ open: true, type: 'carrier', item: row });
                          }} data-testid={`delete-carrier-${row.carrier_id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    )}
                  ]}
                  data={carriers}
                  emptyTitle="No carriers"
                  emptyDescription="Add your first carrier to get started."
                  searchValue={carrierSearch}
                  onSearchChange={setCarrierSearch}
                  searchPlaceholder="Search carriers..."
                  searchFields={['carrier_name', 'notes']}
                  filters={carrierFilterDefs}
                  filterValues={carrierFilters}
                  onFilterChange={(key, val) => setCarrierFilters(prev => ({...prev, [key]: val}))}
                  filterFn={carrierFilterFn}
                  defaultSortKey="carrier_name"
                  defaultSortDir="asc"
                  defaultPageSize={25}
                />
              </TabsContent>

              {/* Contract Types Tab */}
              <TabsContent value="types" className="mt-0">
                <div className="flex justify-end mb-4">
                  <Button onClick={() => {
                    setEditingItem(null);
                    setContractTypeForm({ carrier_id: '', contract_type_name: '', description: '', active: true, product_line: 'ACA' });
                    setShowContractTypeDialog(true);
                  }} data-testid="add-type-btn">
                    <Plus size={16} className="mr-2" />
                    Add Contract Type
                  </Button>
                </div>
                <DataTable
                  columns={[
                    { header: 'Carrier', accessor: 'carrier_id', sortable: true,
                      sortAccessor: (row) => getCarrierName(row.carrier_id),
                      cell: (row) => getCarrierName(row.carrier_id) },
                    { header: 'Description', cell: (row) => row.default_requirements || '-' },
                    { header: 'Product Line', accessor: 'product_line', sortable: true,
                      cell: (row) => {
                        const colors = { ACA: 'bg-emerald-100 text-emerald-700', Medicare: 'bg-blue-100 text-blue-700', Life_Annuity: 'bg-purple-100 text-purple-700', Ancillary: 'bg-amber-100 text-amber-700', Private_Health_Plans: 'bg-teal-100 text-teal-700' };
                        const pl = row.product_line || 'ACA';
                        return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[pl] || 'bg-slate-100 text-slate-600'}`}>{pl === 'Life_Annuity' ? 'Life & Annuity' : pl === 'Private_Health_Plans' ? 'Private Health Plans' : pl}</span>;
                      }
                    },
                    { header: 'Status', accessor: 'active', sortable: true, cell: (row) => <StatusBadge status={row.active ? 'active' : 'inactive'} /> },
                    { header: 'Actions', cell: (row) => (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingItem(row);
                          setContractTypeForm({
                            carrier_id: row.carrier_id,
                            contract_type_name: row.contract_type_name,
                            description: row.default_requirements || '',
                            active: row.active,
                            product_line: row.product_line || 'ACA'
                          });
                          setShowContractTypeDialog(true);
                        }} data-testid={`edit-type-${row.contract_type_id}`}>
                          <Edit size={14} />
                        </Button>
                        {isKR && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => {
                            setDeleteDialog({ open: true, type: 'contractType', item: row });
                          }} data-testid={`delete-type-${row.contract_type_id}`}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    )}
                  ]}
                  data={contractTypes}
                  emptyTitle="No contract types"
                  emptyDescription="Add contract types for each carrier."
                  searchValue={ctSearch}
                  onSearchChange={setCtSearch}
                  searchPlaceholder="Search contract types..."
                  searchFields={[(r) => getCarrierName(r.carrier_id), 'product_line']}
                  filters={ctFilterDefs}
                  filterValues={ctFilters}
                  onFilterChange={(key, val) => setCtFilters(prev => ({...prev, [key]: val}))}
                  filterFn={ctFilterFn}
                  defaultSortKey="product_line"
                  defaultSortDir="asc"
                  defaultPageSize={25}
                />
              </TabsContent>

              {/* Levels Tab - Table-based Inline Editing */}
              <TabsContent value="levels" className="mt-0">
                <div className="space-y-4">
                  {/* Carrier Filter + Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-64">
                        <Select
                          value={levelManagerCarrier || '__all__'}
                          onValueChange={(value) => setLevelManagerCarrier(value === '__all__' ? '' : value)}
                        >
                          <SelectTrigger data-testid="level-carrier-filter">
                            <SelectValue placeholder="All carriers" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All Carriers</SelectItem>
                            {carriers.filter(c => c.active).map(c => (
                              <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {levelManagerCarrier && (
                        <span className="text-sm text-slate-500">
                          {levelEdits.length} level{levelEdits.length !== 1 ? 's' : ''} for {getCarrierName(levelManagerCarrier)}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {hasLevelChanges && (
                        <>
                          <Button variant="outline" onClick={cancelLevelChanges}>
                            <X size={16} className="mr-1" />
                            Cancel
                          </Button>
                          <Button onClick={handleBulkSaveLevels} data-testid="bulk-save-levels">
                            <Save size={16} className="mr-1" />
                            Save All Changes
                          </Button>
                        </>
                      )}
                      <Button variant="outline" onClick={addLevelRow} disabled={!levelManagerCarrier} data-testid="add-level-row">
                        <Plus size={16} className="mr-1" />
                        Add Row
                      </Button>
                    </div>
                  </div>

                  {/* Level Hierarchy Explanation */}
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="py-3">
                      <CardDescription className="text-xs">
                        <strong>Level Position (Internal Hierarchy):</strong> Street Level = 0, Levels above street = positive (+1, +2), Levels below street = negative (-1, -2).
                        This numeric value is for internal use only and is hidden from agents/agencies.
                      </CardDescription>
                    </CardContent>
                  </Card>

                  {/* Inline Editing Table */}
                  {!levelManagerCarrier ? (
                    <div className="text-center py-12 text-slate-400">
                      Select a carrier to manage its levels
                    </div>
                  ) : levelEdits.length === 0 ? (
                    <div className="text-center py-12 text-slate-400">
                      No levels defined for this carrier. Click "Add Row" to create one.
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Level Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Level Code</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Position (Internal)</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Active</th>
                            <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase w-20">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {levelEdits.sort((a, b) => b.level_rank - a.level_rank).map((level, index) => (
                            <tr key={level.level_id || `new-${index}`} className={level._isNew ? 'bg-green-50' : level._modified ? 'bg-amber-50' : ''}>
                              <td className="px-4 py-2">
                                <Input
                                  value={level.level_name}
                                  onChange={(e) => updateLevelRow(index, 'level_name', e.target.value)}
                                  placeholder="e.g., Street Level"
                                  className="h-9"
                                  data-testid={`level-name-${index}`}
                                />
                              </td>
                              <td className="px-4 py-2">
                                <Input
                                  value={level.level_code || ''}
                                  onChange={(e) => updateLevelRow(index, 'level_code', e.target.value)}
                                  placeholder="e.g., SL"
                                  className="h-9 w-24"
                                />
                              </td>
                              <td className="px-4 py-2">
                                <Input
                                  type="number"
                                  value={level.level_rank}
                                  onChange={(e) => updateLevelRow(index, 'level_rank', parseInt(e.target.value) || 0)}
                                  className="h-9 w-24 font-mono"
                                  data-testid={`level-rank-${index}`}
                                />
                              </td>
                              <td className="px-4 py-2 text-center">
                                <Checkbox
                                  checked={level.active}
                                  onCheckedChange={(checked) => updateLevelRow(index, 'active', checked)}
                                />
                              </td>
                              <td className="px-4 py-2 text-center">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => markLevelForDelete(index)}
                                  data-testid={`delete-level-${index}`}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, type: '', item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteDialog.type === 'contractType' ? 'contract type' : deleteDialog.type}? 
              This action cannot be undone.
              {deleteDialog.type === 'carrier' && (
                <span className="block mt-2 text-amber-600">
                  Note: Carriers with active contracts cannot be deleted.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Carrier Dialog */}
      <Dialog open={showCarrierDialog} onOpenChange={setShowCarrierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{editingItem ? 'Edit Carrier' : 'Add Carrier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCarrierSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Carrier Name *</Label>
                <Input
                  value={carrierForm.carrier_name}
                  onChange={(e) => setCarrierForm(prev => ({ ...prev, carrier_name: e.target.value }))}
                  required
                  data-testid="carrier-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={carrierForm.notes}
                  onChange={(e) => setCarrierForm(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
              {editingItem && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={carrierForm.active}
                    onCheckedChange={(checked) => setCarrierForm(prev => ({ ...prev, active: checked }))}
                  />
                  <Label>Active</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCarrierDialog(false)}>Cancel</Button>
              <Button type="submit">{editingItem ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contract Type Dialog */}
      <Dialog open={showContractTypeDialog} onOpenChange={setShowContractTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{editingItem ? 'Edit Contract Type' : 'Add Contract Type'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleContractTypeSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Carrier *</Label>
                <Select
                  value={contractTypeForm.carrier_id}
                  onValueChange={(value) => setContractTypeForm(prev => ({ ...prev, carrier_id: value }))}
                  disabled={!!editingItem}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select carrier" />
                  </SelectTrigger>
                  <SelectContent>
                    {carriers.filter(c => c.active).map(c => (
                      <SelectItem key={c.carrier_id} value={c.carrier_id}>{c.carrier_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Product Line *</Label>
                <Select
                  value={contractTypeForm.product_line}
                  onValueChange={(value) => setContractTypeForm(prev => ({ ...prev, product_line: value }))}
                >
                  <SelectTrigger data-testid="product-line-select">
                    <SelectValue placeholder="Select product line" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCT_LINES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">
                  The Contract Type is defined by the Product Line — one type per carrier.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={contractTypeForm.description}
                  onChange={(e) => setContractTypeForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Optional description of this contract type"
                />
              </div>
              {editingItem && (
                <div className="flex items-center gap-2">
                  <Switch
                    checked={contractTypeForm.active}
                    onCheckedChange={(checked) => setContractTypeForm(prev => ({ ...prev, active: checked }))}
                  />
                  <Label>Active</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowContractTypeDialog(false)}>Cancel</Button>
              <Button type="submit">{editingItem ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
